<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left"><h1 class="page-title">編輯門市預約設定</h1></div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" :disabled="saving" @click="goBack">取消</button>
        <button type="button" class="btn btn-primary" :disabled="loading || saving" @click="save">
          {{ saving ? '儲存中...' : '儲存設定' }}
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>載入中...</p></div>
      <form v-else class="form-container salon-form-page" @submit.prevent="save">
        <div class="store-summary">
          <i class="ri-store-2-line"></i>
          <div>
            <strong>{{ storeName }}</strong>
            <span>以下預約參數、排班模式與營業時段皆以此門市為準。</span>
            <template v-if="bookingUrl">
              <div v-for="link in bookingLinks" :key="link.key" class="booking-link">
                <span class="booking-link-label">{{ link.label }}</span>
                <div class="booking-link-field">
                  <span class="booking-link-text">{{ link.url }}</span>
                  <button
                    type="button"
                    class="booking-copy"
                    :class="{ copied: copiedLinkKey === link.key }"
                    @click="copyBookingUrl(link)"
                  >
                    <i :class="copiedLinkKey === link.key ? 'ri-check-line' : 'ri-file-copy-line'"></i>
                    {{ copiedLinkKey === link.key ? '已複製' : '複製' }}
                  </button>
                </div>
                <span
                  v-if="(link.key === 'store' || link.key === 'store-stylists') && !form.bookingEnabled"
                  class="booking-link-hint"
                >
                  此門市尚未開放預約，顧客開啟後會改為選擇其他門市。
                </span>
              </div>
            </template>
            <span v-else-if="bookingSlug">請先到 LINE 設定填寫共用入口 LIFF ID，才能產生顧客預約連結。</span>
          </div>
        </div>

        <div class="tabs-nav" role="tablist" aria-label="門市預約設定">
          <button
            type="button"
            class="tabs-nav-item"
            :class="{ active: activeTab === 'settings' }"
            @click="activeTab = 'settings'"
          >
            預約與排班設定
          </button>
          <button
            type="button"
            class="tabs-nav-item"
            :class="{ active: activeTab === 'hours' }"
            @click="activeTab = 'hours'"
          >
            營業時間
          </button>
        </div>

        <div v-if="activeTab === 'settings'" class="form-section">
          <div class="booking-switch-panel">
            <label class="booking-switch" :class="{ on: form.bookingEnabled }">
              <span>開放此門市美髮預約</span>
              <span class="toggle-switch">
                <input v-model="form.bookingEnabled" type="checkbox" />
                <span class="toggle-slider"></span>
              </span>
            </label>
            <label class="booking-switch" :class="{ on: form.requireMemberRegistration }">
              <span>預約前必須註冊會員</span>
              <span class="toggle-switch">
                <input v-model="form.requireMemberRegistration" type="checkbox" />
                <span class="toggle-slider"></span>
              </span>
            </label>
            <label class="booking-switch" :class="{ on: form.allowStylistSelection }">
              <span>允許指定設計師</span>
              <span class="toggle-switch">
                <input v-model="form.allowStylistSelection" type="checkbox" />
                <span class="toggle-slider"></span>
              </span>
            </label>
            <label class="booking-switch" :class="{ on: form.allowNoPreference }">
              <span>允許不指定設計師</span>
              <span class="toggle-switch">
                <input v-model="form.allowNoPreference" type="checkbox" />
                <span class="toggle-slider"></span>
              </span>
            </label>
          </div>
          <h2 class="section-title">門市預約與排班規則</h2>
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label">排班模式</label>
              <select v-model="form.scheduleMode" class="form-control">
                <option value="monthly">月排班</option>
                <option value="weekly">週排班</option>
              </select>
              <small class="form-hint">此門市的所有設計師班表皆依此模式管理。</small>
            </div>
            <div class="form-group">
              <label class="form-label" :class="{ 'required-label': scheduleModeChangeRequiresDate }">模式切換生效日</label>
              <input
                v-model="form.scheduleModeEffectiveFrom"
                type="date"
                class="form-control"
                :min="tomorrowDate"
              />
              <small class="form-hint">切換排班模式時必須指定未來生效日。</small>
            </div>
            <div class="form-group">
              <label class="form-label required-label">預約時段單位（分鐘）</label>
              <input v-model.number="form.bookingSlotMinutes" type="number" class="form-control" min="5" max="120" step="5" />
            </div>
            <div class="form-group">
              <label class="form-label required-label">設計師同時服務預設上限</label>
              <input v-model.number="form.defaultConcurrencyLimit" type="number" class="form-control" min="1" max="20" />
              <small class="form-hint">套用至此門市所有設計師；設計師另有設定時，以設計師的覆蓋值為準。</small>
            </div>
            <div class="form-group">
              <label class="form-label required-label">最早提前分鐘</label>
              <input v-model.number="form.minAdvanceMinutes" type="number" class="form-control" min="0" />
            </div>
            <div class="form-group">
              <label class="form-label required-label">最遠預約天數</label>
              <input v-model.number="form.maxFutureBookingDays" type="number" class="form-control" min="1" max="730" />
            </div>
            <div class="form-group">
              <label class="form-label required-label">時區</label>
              <input v-model.trim="form.timezone" class="form-control" maxlength="64" />
            </div>
          </div>
          <div
            v-if="pendingModeSwitch && !pendingModeSwitch.confirmed"
            class="mode-switch-alert warning mode-switch-alert-block"
          >
            <div class="mode-switch-alert-head">
              <i class="ri-error-warning-line"></i>
              <div>
                <strong>排班模式切換尚未確認</strong>
                <span>
                  請先發布 {{ pendingModeSwitch.mode === 'weekly' ? '週排班' : '月排班' }}
                  並處理既有預約衝突；確認完成前仍使用原排班模式。
                </span>
              </div>
              <router-link
                :to="{ name: 'SalonStoreSchedules', params: { storeId: $route.params.storeId } }"
                class="btn btn-sm btn-secondary"
              >
                前往班表
              </router-link>
            </div>
            <div v-if="missingScheduleNames.length" class="mode-switch-detail">
              <h4>尚缺新模式班表（{{ missingScheduleNames.length }} 位）</h4>
              <p>{{ missingScheduleNames.join('、') }}</p>
            </div>
            <div v-if="reservationConflicts.length" class="mode-switch-detail">
              <h4>衝突預約（{{ reservationConflicts.length }} 筆）</h4>
              <div class="table-wrapper conflict-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>預約編號</th>
                      <th>客人</th>
                      <th>電話</th>
                      <th>設計師</th>
                      <th>日期</th>
                      <th>時間</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in reservationConflicts" :key="item.id || item.reservationNumber">
                      <td>{{ item.reservationNumber || '-' }}</td>
                      <td>{{ item.contactName || '未填姓名' }}</td>
                      <td>{{ item.contactPhone || '-' }}</td>
                      <td>{{ item.stylistName || '-' }}</td>
                      <td>{{ dateOnly(item.appointmentDate) }}</td>
                      <td>{{ shortTime(item.startTime) }}－{{ shortTime(item.endTime) }}</td>
                      <td>
                        <router-link
                          v-if="item.id"
                          :to="{ name: 'SalonReservationEdit', params: { id: item.id } }"
                          class="btn btn-sm btn-primary"
                        >
                          處理
                        </router-link>
                        <span v-else>-</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div
            v-else-if="pendingModeSwitch?.confirmed"
            class="mode-switch-alert confirmed"
          >
            <i class="ri-checkbox-circle-line"></i>
            <div>
              <strong>
                {{ pendingModeSwitch.effectiveFrom <= todayDate
                  ? '排班模式切換已生效'
                  : '排班模式切換已確認' }}
              </strong>
              <span v-if="pendingModeSwitch.effectiveFrom <= todayDate">
                新模式已於 {{ pendingModeSwitch.effectiveFrom }} 生效。
              </span>
              <span v-else>
                新模式將於 {{ pendingModeSwitch.effectiveFrom }} 生效，生效日前仍使用原模式。
              </span>
            </div>
          </div>
          <div class="notification-panel">
            <label class="check-label notification-master">
              <input v-model="form.notifyStylistOnNewReservation" type="checkbox" />
              有新預約時通知設計師
            </label>
            <p class="form-hint">依下列順序嘗試通知，任一管道成功後即停止；未勾選的管道不會使用。</p>
            <div class="notification-channel-list" :class="{ disabled: !form.notifyStylistOnNewReservation }">
              <div
                v-for="(channel, index) in notificationChannels"
                :key="channel.value"
                class="notification-channel-row"
              >
                <span class="channel-order">{{ index + 1 }}</span>
                <label class="check-label">
                  <input
                    v-model="channel.enabled"
                    type="checkbox"
                    :disabled="!form.notifyStylistOnNewReservation"
                  />
                  <i :class="channel.icon"></i>
                  {{ channel.label }}
                </label>
                <div class="channel-order-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-secondary"
                    :disabled="!form.notifyStylistOnNewReservation || index === 0"
                    :aria-label="`${channel.label}往上移`"
                    @click="moveNotificationChannel(index, -1)"
                  >
                    <i class="ri-arrow-up-line"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-secondary"
                    :disabled="!form.notifyStylistOnNewReservation || index === notificationChannels.length - 1"
                    :aria-label="`${channel.label}往下移`"
                    @click="moveNotificationChannel(index, 1)"
                  >
                    <i class="ri-arrow-down-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="notification-panel">
            <label class="check-label notification-master">
              <input v-model="form.notifyCustomerOnNewReservation" type="checkbox" />
              消費者完成預約時通知顧客
            </label>
            <p class="form-hint">依下列順序嘗試通知，任一管道成功後即停止；未勾選的管道不會使用。</p>
            <div class="notification-channel-list" :class="{ disabled: !form.notifyCustomerOnNewReservation }">
              <div
                v-for="(channel, index) in confirmationNotificationChannels"
                :key="channel.value"
                class="notification-channel-row"
              >
                <span class="channel-order">{{ index + 1 }}</span>
                <label class="check-label">
                  <input
                    v-model="channel.enabled"
                    type="checkbox"
                    :disabled="!form.notifyCustomerOnNewReservation"
                  />
                  <i :class="channel.icon"></i>
                  {{ channel.label }}
                </label>
                <div class="channel-order-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-secondary"
                    :disabled="!form.notifyCustomerOnNewReservation || index === 0"
                    :aria-label="`${channel.label}往上移`"
                    @click="moveConfirmationNotificationChannel(index, -1)"
                  >
                    <i class="ri-arrow-up-line"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-secondary"
                    :disabled="!form.notifyCustomerOnNewReservation || index === confirmationNotificationChannels.length - 1"
                    :aria-label="`${channel.label}往下移`"
                    @click="moveConfirmationNotificationChannel(index, 1)"
                  >
                    <i class="ri-arrow-down-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="notification-panel">
            <label class="check-label notification-master">
              <input v-model="form.notifyCustomerDayBefore" type="checkbox" />
              在預約前一天通知顧客
            </label>
            <div class="form-group reminder-time-field">
              <label class="form-label" :class="{ 'required-label': form.notifyCustomerDayBefore }">發送時間</label>
              <select
                v-model="form.customerReminderTime"
                class="form-control"
                :disabled="!form.notifyCustomerDayBefore"
              >
                <option v-for="hour in reminderHours" :key="hour" :value="hour">{{ hour }}</option>
              </select>
              <span class="form-hint">每小時整點檢查一次，請選擇整點。</span>
            </div>
            <p class="form-hint">依下列順序嘗試通知，任一管道成功後即停止；未勾選的管道不會使用。</p>
            <div class="notification-channel-list" :class="{ disabled: !form.notifyCustomerDayBefore }">
              <div
                v-for="(channel, index) in customerNotificationChannels"
                :key="channel.value"
                class="notification-channel-row"
              >
                <span class="channel-order">{{ index + 1 }}</span>
                <label class="check-label">
                  <input
                    v-model="channel.enabled"
                    type="checkbox"
                    :disabled="!form.notifyCustomerDayBefore"
                  />
                  <i :class="channel.icon"></i>
                  {{ channel.label }}
                </label>
                <div class="channel-order-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-secondary"
                    :disabled="!form.notifyCustomerDayBefore || index === 0"
                    :aria-label="`${channel.label}往上移`"
                    @click="moveCustomerNotificationChannel(index, -1)"
                  >
                    <i class="ri-arrow-up-line"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-secondary"
                    :disabled="!form.notifyCustomerDayBefore || index === customerNotificationChannels.length - 1"
                    :aria-label="`${channel.label}往下移`"
                    @click="moveCustomerNotificationChannel(index, 1)"
                  >
                    <i class="ri-arrow-down-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="copy-panel">
            <div class="copy-panel-header">
              <div>
                <h3>快速套用預約與排班設定</h3>
                <p class="form-hint">複製目前畫面的預約參數與排班模式，不會變更其他門市的營業時間。</p>
              </div>
              <button
                type="button"
                class="btn btn-secondary"
                :disabled="copying || !otherStores.length"
                @click="applySettingsToAllStores"
              >
                {{ copying ? '套用中...' : '套用到全部其他門市' }}
              </button>
            </div>
            <div v-if="otherStores.length" class="copy-targets">
              <div class="copy-target-tools">
                <button type="button" class="btn-link" :disabled="copying" @click="selectAllStores">全選</button>
                <button type="button" class="btn-link" :disabled="copying" @click="targetStoreIds = []">清除</button>
              </div>
              <label v-for="store in otherStores" :key="store.id" class="copy-target">
                <input v-model="targetStoreIds" type="checkbox" :value="store.id" :disabled="copying" />
                {{ store.storeName }}
              </label>
            </div>
            <div class="copy-selected-action">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="copying || !targetStoreIds.length"
                @click="applySettingsToSelectedStores"
              >
                套用到選取門市（{{ targetStoreIds.length }}）
              </button>
            </div>
          </div>
        </div>

        <div v-else class="form-section">
          <h2 class="section-title">門市營業時間</h2>
          <p class="form-hint">共用門市的營業時間設定；每天可設定多個營業時段，排班及可預約時段均以此處為準。</p>
          <div class="hours-table">
            <table>
              <thead>
                <tr><th>星期</th><th>是否營業</th><th>營業時段</th></tr>
              </thead>
              <tbody>
                <tr v-for="(day, dayIndex) in days" :key="day.value">
                  <td class="day-name">{{ day.label }}</td>
                  <td>
                    <label class="check-label">
                      <input v-model="hours[dayIndex].isOpen" type="checkbox" @change="handleDayToggle(dayIndex)" />
                      {{ hours[dayIndex].isOpen ? '營業' : '休息' }}
                    </label>
                  </td>
                  <td>
                    <div v-if="hours[dayIndex].isOpen" class="slots-container">
                      <div
                        v-for="(slot, slotIndex) in hours[dayIndex].timeSlots"
                        :key="`${day.value}-${slotIndex}`"
                        class="slot-row"
                      >
                        <input v-model="slot.openTime" type="time" class="form-control time-input" />
                        <span>～</span>
                        <input v-model="slot.closeTime" type="time" class="form-control time-input" />
                        <button
                          type="button"
                          class="btn btn-sm btn-danger"
                          :disabled="hours[dayIndex].timeSlots.length <= 1"
                          @click="removeSlot(dayIndex, slotIndex)"
                        >
                          刪除
                        </button>
                      </div>
                      <div class="slot-actions">
                        <button type="button" class="btn btn-sm btn-secondary" @click="addSlot(dayIndex)">
                          <i class="ri-add-line"></i> 新增時段
                        </button>
                        <button type="button" class="btn btn-sm btn-secondary" @click="copyDaySlots(dayIndex, 'weekdays')">複製到平日</button>
                        <button type="button" class="btn btn-sm btn-secondary" @click="copyDaySlots(dayIndex, 'weekend')">複製到假日</button>
                        <button type="button" class="btn btn-sm btn-secondary" @click="copyDaySlots(dayIndex, 'all')">複製到全部</button>
                      </div>
                    </div>
                    <span v-else class="closed-text">休息日</span>
                    <div v-if="errors[`day${dayIndex}`]" class="invalid-feedback">{{ errors[`day${dayIndex}`] }}</div>
                    <div
                      v-for="(slot, slotIndex) in hours[dayIndex].timeSlots"
                      :key="`error-${day.value}-${slotIndex}`"
                    >
                      <div v-if="errors[`day${dayIndex}_slot${slotIndex}`]" class="invalid-feedback">
                        {{ errors[`day${dayIndex}_slot${slotIndex}`] }}
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="copy-panel">
            <div class="copy-panel-header">
              <div>
                <h3>快速套用營業時間</h3>
                <p class="form-hint">只複製目前門市的營業時段，不會變更其他門市的排班模式或預約參數。</p>
              </div>
              <button
                type="button"
                class="btn btn-secondary"
                :disabled="copying || !otherStores.length"
                @click="applyHoursToAllStores"
              >
                {{ copying ? '套用中...' : '套用到全部其他門市' }}
              </button>
            </div>
            <div v-if="otherStores.length" class="copy-targets">
              <div class="copy-target-tools">
                <button type="button" class="btn-link" :disabled="copying" @click="selectAllStores">全選</button>
                <button type="button" class="btn-link" :disabled="copying" @click="targetStoreIds = []">清除</button>
              </div>
              <label v-for="store in otherStores" :key="store.id" class="copy-target">
                <input v-model="targetStoreIds" type="checkbox" :value="store.id" :disabled="copying" />
                {{ store.storeName }}
              </label>
            </div>
            <div class="copy-selected-action">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="copying || !targetStoreIds.length"
                @click="applyHoursToSelectedStores"
              >
                套用到選取門市（{{ targetStoreIds.length }}）
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { salonService } from '@/api/modules/salon.js';
import { storeService } from '@/api/modules/stores.js';
import { salonDateOnly } from '@/utils/salonDate.js';
import { fireSwalDeferred, showConfirm, showError, showSuccess } from '@/utils/swal.js';

const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, character => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
}[character]));

const days = [
  { label: '週日', value: 0 }, { label: '週一', value: 1 },
  { label: '週二', value: 2 }, { label: '週三', value: 3 },
  { label: '週四', value: 4 }, { label: '週五', value: 5 },
  { label: '週六', value: 6 }
];
const reminderHours = Array.from({ length: 24 }, (_, hour) => (
  `${String(hour).padStart(2, '0')}:00`
));
const emptyForm = () => ({
  bookingEnabled: false,
  scheduleMode: 'monthly',
  scheduleModeEffectiveFrom: '',
  bookingSlotMinutes: 30,
  defaultConcurrencyLimit: 1,
  allowStylistSelection: true,
  allowNoPreference: true,
  requireMemberRegistration: true,
  minAdvanceMinutes: 0,
  maxFutureBookingDays: 90,
  timezone: 'Asia/Taipei',
  notifyStylistOnNewReservation: false,
  notifyCustomerOnNewReservation: false,
  notifyCustomerDayBefore: false,
  customerReminderTime: '18:00'
});
const defaultNotificationChannels = () => [
  { value: 'line', label: 'LINE', icon: 'ri-line-line', enabled: true },
  { value: 'sms', label: '簡訊', icon: 'ri-message-2-line', enabled: true },
  { value: 'email', label: 'Email', icon: 'ri-mail-line', enabled: true }
];
const defaultCustomerNotificationChannels = () => [
  { value: 'line', label: 'LINE', icon: 'ri-line-line', enabled: true },
  { value: 'sms', label: '簡訊', icon: 'ri-message-2-line', enabled: true }
];
const defaultSlot = () => ({ openTime: '09:00', closeTime: '18:00' });
const defaultDay = dayOfWeek => ({ dayOfWeek, isOpen: false, timeSlots: [] });
const defaultHours = () => days.map(day => defaultDay(day.value));

function stylistsUrlFromBooking(bookingUrl, storeId) {
  if (!bookingUrl) return '';
  try {
    const url = new URL(bookingUrl);
    if (!/\/stylists\/?$/.test(url.pathname)) {
      url.pathname = `${url.pathname.replace(/\/$/, '')}/stylists`;
    }
    if (storeId) url.searchParams.set('store', storeId);
    else url.searchParams.delete('store');
    return url.toString();
  } catch {
    const [base] = bookingUrl.split('?');
    const stylistsBase = `${base.replace(/\/$/, '')}/stylists`;
    return storeId
      ? `${stylistsBase}?store=${encodeURIComponent(storeId)}`
      : stylistsBase;
  }
}

export default {
  name: 'SalonSettingsForm',
  data() {
    return {
      loading: false,
      saving: false,
      copying: false,
      activeTab: 'settings',
      reminderHours,
      storeName: '',
      bookingSlug: '',
      bookingPath: '',
      bookingUrl: '',
      storeBookingUrl: '',
      copiedLinkKey: '',
      stores: [],
      targetStoreIds: [],
      form: emptyForm(),
      notificationChannels: defaultNotificationChannels(),
      confirmationNotificationChannels: defaultCustomerNotificationChannels(),
      customerNotificationChannels: defaultCustomerNotificationChannels(),
      pendingModeSwitch: null,
      modeSwitchReadiness: null,
      activeScheduleMode: 'monthly',
      days,
      hours: defaultHours(),
      errors: {}
    };
  },
  computed: {
    reservationConflicts() {
      return this.modeSwitchReadiness?.reservationConflicts || [];
    },
    missingScheduleNames() {
      return (this.modeSwitchReadiness?.missingSchedules || [])
        .map(item => item.stylistName)
        .filter(Boolean);
    },
    otherStores() {
      return this.stores.filter(store => store.id !== this.$route.params.storeId);
    },
    bookingLinks() {
      const allStylistsUrl = stylistsUrlFromBooking(this.bookingUrl);
      const storeStylistsUrl = stylistsUrlFromBooking(this.bookingUrl, this.$route.params.storeId)
        || stylistsUrlFromBooking(this.storeBookingUrl);
      return [
        { key: 'all', label: '全部門市預約連結（顧客先選擇門市）', url: this.bookingUrl },
        { key: 'store', label: '本門市預約連結（直接進入此門市）', url: this.storeBookingUrl },
        { key: 'all-stylists', label: '全部門市設計師介紹連結（顧客先選擇門市）', url: allStylistsUrl },
        { key: 'store-stylists', label: '本門市設計師介紹連結（直接進入此門市）', url: storeStylistsUrl }
      ].filter(link => link.url);
    },
    tomorrowDate() {
      const today = salonDateOnly(new Date());
      const tomorrow = new Date(`${today}T00:00:00Z`);
      tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
      return tomorrow.toISOString().slice(0, 10);
    },
    todayDate() {
      return salonDateOnly(new Date());
    },
    scheduleModeChangeRequiresDate() {
      return this.form.scheduleMode !== this.activeScheduleMode;
    }
  },
  async mounted() {
    this.loading = true;
    try {
      const storesResponse = await storeService.getStores({ page: 1, pageSize: 100 });
      this.stores = storesResponse?.data?.items || [];
      const store = this.stores.find(item => item.id === this.$route.params.storeId);
      this.storeName = store?.storeName || '未知門市';
      await Promise.all([this.loadSettings(), this.loadHours()]);
    } catch (error) {
      await this.handleError(error, '門市預約設定載入失敗');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async loadSettings() {
      try {
        const response = await salonService.getSettings(this.$route.params.storeId);
        this.form = {
          ...emptyForm(),
          ...response.data,
          scheduleMode: response.data.pendingScheduleMode || response.data.scheduleMode,
          scheduleModeEffectiveFrom: salonDateOnly(response.data.scheduleModeEffectiveFrom),
          bookingSlotMinutes: Number(response.data.bookingSlotMinutes || 30),
          defaultConcurrencyLimit: Number(response.data.defaultConcurrencyLimit || 1),
          minAdvanceMinutes: Number(response.data.minAdvanceMinutes || 0),
          maxFutureBookingDays: Number(response.data.maxFutureBookingDays || 90),
          customerReminderTime: this.wholeHour(response.data.customerReminderTime || '18:00')
        };
        this.setNotificationChannels(response.data.stylistNotificationChannels);
        this.setConfirmationNotificationChannels(
          response.data.customerConfirmationNotificationChannels
        );
        this.setCustomerNotificationChannels(response.data.customerNotificationChannels);
        this.bookingSlug = response.data.bookingSlug || '';
        this.bookingPath = response.data.bookingPath || '';
        this.bookingUrl = response.data.bookingUrl || '';
        this.storeBookingUrl = response.data.storeBookingUrl || '';
        this.activeScheduleMode = response.data.scheduleMode || 'monthly';
        this.setPendingModeSwitch(response.data);
      } catch (error) {
        if (error.response?.status !== 404) throw error;
      }
    },
    async loadHours() {
      const response = await storeService.getStoreHours(this.$route.params.storeId);
      const loaded = response?.data?.hours || [];
      this.hours = defaultHours();
      days.forEach((day, index) => {
        const current = loaded.find(item => Number(item.dayOfWeek) === day.value);
        if (!current) return;
        let slots = [];
        if (Array.isArray(current.timeSlots) && current.timeSlots.length) {
          slots = current.timeSlots
            .filter(slot => slot?.openTime && slot?.closeTime)
            .map(slot => ({
              displayName: slot.displayName || '',
              openTime: this.shortTime(slot.openTime),
              closeTime: this.shortTime(slot.closeTime)
            }));
        } else if (current.openTime && current.closeTime) {
          if (current.breakStart && current.breakEnd) {
            slots = [
              { openTime: this.shortTime(current.openTime), closeTime: this.shortTime(current.breakStart) },
              { openTime: this.shortTime(current.breakEnd), closeTime: this.shortTime(current.closeTime) }
            ];
          } else {
            slots = [{
              openTime: this.shortTime(current.openTime),
              closeTime: this.shortTime(current.closeTime)
            }];
          }
        }
        this.hours[index] = {
          dayOfWeek: day.value,
          isOpen: Boolean(current.isOpen || slots.length),
          timeSlots: slots.length ? slots : (current.isOpen ? [defaultSlot()] : [])
        };
      });
    },
    handleDayToggle(dayIndex) {
      const day = this.hours[dayIndex];
      day.timeSlots = day.isOpen ? (day.timeSlots.length ? day.timeSlots : [defaultSlot()]) : [];
    },
    addSlot(dayIndex) {
      this.hours[dayIndex].timeSlots.push(defaultSlot());
    },
    removeSlot(dayIndex, slotIndex) {
      if (this.hours[dayIndex].timeSlots.length > 1) {
        this.hours[dayIndex].timeSlots.splice(slotIndex, 1);
      }
    },
    copyDaySlots(sourceIndex, mode) {
      const source = this.hours[sourceIndex];
      const targets = mode === 'weekdays' ? [1, 2, 3, 4, 5]
        : mode === 'weekend' ? [0, 6] : [0, 1, 2, 3, 4, 5, 6];
      targets.forEach(index => {
        if (index === sourceIndex) return;
        this.hours[index].isOpen = source.isOpen;
        this.hours[index].timeSlots = source.isOpen
          ? source.timeSlots.map(slot => ({ ...slot }))
          : [];
      });
    },
    validateHours() {
      this.errors = {};
      this.hours.forEach((day, dayIndex) => {
        if (!day.isOpen) return;
        if (!day.timeSlots.length) {
          this.errors[`day${dayIndex}`] = '請至少新增一個營業時段';
          return;
        }
        const sorted = day.timeSlots
          .map((slot, index) => ({ ...slot, index }))
          .sort((a, b) => a.openTime.localeCompare(b.openTime));
        sorted.forEach((slot, sortedIndex) => {
          if (!slot.openTime || !slot.closeTime) {
            this.errors[`day${dayIndex}_slot${slot.index}`] = '請輸入完整開始與結束時間';
          } else if (slot.openTime >= slot.closeTime) {
            this.errors[`day${dayIndex}_slot${slot.index}`] = '結束時間必須晚於開始時間';
          } else if (sortedIndex > 0 && slot.openTime < sorted[sortedIndex - 1].closeTime) {
            this.errors[`day${dayIndex}_slot${slot.index}`] = '營業時段不可重疊';
          }
        });
      });
      return Object.keys(this.errors).length === 0;
    },
    hoursPayload() {
      return this.hours
        .filter(day => day.isOpen)
        .map(day => ({
          dayOfWeek: day.dayOfWeek,
          isOpen: true,
          timeSlots: [...day.timeSlots]
            .sort((a, b) => a.openTime.localeCompare(b.openTime))
            .map(slot => ({
              displayName: String(slot.displayName || '').trim(),
              openTime: slot.openTime,
              closeTime: slot.closeTime
            }))
        }));
    },
    selectAllStores() {
      this.targetStoreIds = this.otherStores.map(store => store.id);
    },
    setNotificationChannels(selected = []) {
      const selectedValues = Array.isArray(selected) ? selected : [];
      const defaults = defaultNotificationChannels();
      const byValue = new Map(defaults.map(item => [item.value, item]));
      this.notificationChannels = [
        ...selectedValues.map(value => ({ ...byValue.get(value), enabled: true })),
        ...defaults
          .filter(item => !selectedValues.includes(item.value))
          .map(item => ({ ...item, enabled: false }))
      ].filter(item => item.value);
    },
    moveNotificationChannel(index, offset) {
      const target = index + offset;
      if (target < 0 || target >= this.notificationChannels.length) return;
      const next = [...this.notificationChannels];
      [next[index], next[target]] = [next[target], next[index]];
      this.notificationChannels = next;
    },
    setConfirmationNotificationChannels(selected = []) {
      this.confirmationNotificationChannels = this.orderedChannels(
        defaultCustomerNotificationChannels(),
        selected
      );
    },
    moveConfirmationNotificationChannel(index, offset) {
      this.confirmationNotificationChannels = this.movedChannels(
        this.confirmationNotificationChannels,
        index,
        offset
      );
    },
    orderedChannels(defaults, selected = []) {
      const selectedValues = Array.isArray(selected) ? selected : [];
      const byValue = new Map(defaults.map(item => [item.value, item]));
      return [
        ...selectedValues.map(value => ({ ...byValue.get(value), enabled: true })),
        ...defaults
          .filter(item => !selectedValues.includes(item.value))
          .map(item => ({ ...item, enabled: false }))
      ].filter(item => item.value);
    },
    movedChannels(channels, index, offset) {
      const target = index + offset;
      if (target < 0 || target >= channels.length) return channels;
      const next = [...channels];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    },
    setCustomerNotificationChannels(selected = []) {
      this.customerNotificationChannels = this.orderedChannels(
        defaultCustomerNotificationChannels(),
        selected
      );
    },
    moveCustomerNotificationChannel(index, offset) {
      const target = index + offset;
      if (target < 0 || target >= this.customerNotificationChannels.length) return;
      const next = [...this.customerNotificationChannels];
      [next[index], next[target]] = [next[target], next[index]];
      this.customerNotificationChannels = next;
    },
    settingsPayload() {
      return {
        bookingEnabled: this.form.bookingEnabled,
        scheduleMode: this.form.scheduleMode,
        scheduleModeEffectiveFrom: this.form.scheduleModeEffectiveFrom || null,
        bookingSlotMinutes: Number(this.form.bookingSlotMinutes),
        defaultConcurrencyLimit: Number(this.form.defaultConcurrencyLimit),
        allowStylistSelection: this.form.allowStylistSelection,
        allowNoPreference: this.form.allowNoPreference,
        requireMemberRegistration: this.form.requireMemberRegistration,
        minAdvanceMinutes: Number(this.form.minAdvanceMinutes),
        maxFutureBookingDays: Number(this.form.maxFutureBookingDays),
        timezone: this.form.timezone,
        notifyStylistOnNewReservation: this.form.notifyStylistOnNewReservation,
        stylistNotificationChannels: this.notificationChannels
          .filter(channel => channel.enabled)
          .map(channel => channel.value),
        notifyCustomerOnNewReservation: this.form.notifyCustomerOnNewReservation,
        customerConfirmationNotificationChannels: this.confirmationNotificationChannels
          .filter(channel => channel.enabled)
          .map(channel => channel.value),
        notifyCustomerDayBefore: this.form.notifyCustomerDayBefore,
        customerReminderTime: this.form.customerReminderTime,
        customerNotificationChannels: this.customerNotificationChannels
          .filter(channel => channel.enabled)
          .map(channel => channel.value)
      };
    },
    async applySettingsToAllStores() {
      await this.applySettingsToStores(this.otherStores.map(store => store.id), '全部其他門市');
    },
    async applySettingsToSelectedStores() {
      await this.applySettingsToStores(this.targetStoreIds, `${this.targetStoreIds.length} 間選取門市`);
    },
    async applySettingsToStores(storeIds, targetLabel) {
      if (!storeIds.length || this.copying) return;
      if (!this.validateConcurrencyLimit()) return;
      if (!this.validateNotificationSettings()) return;
      const confirmed = await showConfirm(
        '套用預約與排班設定？',
        `系統會先儲存目前門市，再以目前畫面的預約參數與排班模式覆蓋${targetLabel}；營業時間不受影響。`,
        '確認套用'
      );
      if (!confirmed) return;

      this.copying = true;
      try {
        const payload = this.settingsPayload();
        await salonService.updateSettings(this.$route.params.storeId, payload);
        for (const storeId of storeIds) {
          await salonService.updateSettings(storeId, payload);
        }
        await showSuccess('套用成功', `目前門市已儲存，並已更新${targetLabel}的預約與排班設定`);
        this.targetStoreIds = [];
      } catch (error) {
        await this.handleError(error, '預約與排班設定套用失敗');
      } finally {
        this.copying = false;
      }
    },
    async applyHoursToAllStores() {
      await this.applyHoursToStores(this.otherStores.map(store => store.id), '全部其他門市');
    },
    async applyHoursToSelectedStores() {
      await this.applyHoursToStores(this.targetStoreIds, `${this.targetStoreIds.length} 間選取門市`);
    },
    async applyHoursToStores(storeIds, targetLabel) {
      if (!storeIds.length || this.copying) return;
      if (!this.validateHours()) {
        await showError('驗證失敗', '請先修正目前門市的營業時段。');
        return;
      }
      const confirmed = await showConfirm(
        '套用營業時間？',
        `系統會先儲存目前門市，再以目前畫面的營業時間覆蓋${targetLabel}；排班模式與預約參數不受影響。`,
        '確認套用'
      );
      if (!confirmed) return;

      this.copying = true;
      try {
        const payload = this.hoursPayload();
        await storeService.setStoreHours(this.$route.params.storeId, payload);
        for (const storeId of storeIds) {
          await storeService.setStoreHours(storeId, payload);
        }
        await showSuccess('套用成功', `目前門市已儲存，並已更新${targetLabel}的營業時間`);
        this.targetStoreIds = [];
      } catch (error) {
        await this.handleError(error, '營業時間套用失敗');
      } finally {
        this.copying = false;
      }
    },
    async save() {
      if (this.saving) return;
      if (!this.validateConcurrencyLimit()) return;
      if (!this.validateNotificationSettings()) return;
      if (!this.validateHours()) {
        this.activeTab = 'hours';
        await showError('驗證失敗', '請檢查門市營業時段。');
        return;
      }
      this.saving = true;
      try {
        const settingsResponse = await salonService.updateSettings(
          this.$route.params.storeId,
          this.settingsPayload()
        );
        this.setPendingModeSwitch(settingsResponse.data);
        this.bookingSlug = settingsResponse.data.bookingSlug || this.bookingSlug;
        this.bookingPath = settingsResponse.data.bookingPath || this.bookingPath;
        this.bookingUrl = settingsResponse.data.bookingUrl || this.bookingUrl;
        this.storeBookingUrl = settingsResponse.data.storeBookingUrl || this.storeBookingUrl;
        await storeService.setStoreHours(this.$route.params.storeId, this.hoursPayload());
        if (settingsResponse.data.pendingScheduleMode
          && !settingsResponse.data.scheduleModeSwitchConfirmed) {
          await this.showModeSwitchBlockers(settingsResponse.data.modeSwitchReadiness || {});
        } else {
          await showSuccess('更新成功', '門市預約、排班模式與營業時間已儲存');
          this.goBack();
        }
      } catch (error) {
        await this.handleError(error, '門市預約設定儲存失敗');
      } finally {
        this.saving = false;
      }
    },
    validateNotificationSettings() {
      if (this.form.notifyStylistOnNewReservation
        && !this.notificationChannels.some(channel => channel.enabled)) {
        showError('驗證失敗', '啟用設計師通知時，請至少勾選一個通知管道。');
        return false;
      }
      if (this.form.notifyCustomerOnNewReservation
        && !this.confirmationNotificationChannels.some(channel => channel.enabled)) {
        showError('驗證失敗', '啟用預約完成通知時，請至少勾選一個通知管道。');
        return false;
      }
      if (this.form.notifyCustomerDayBefore && !this.form.customerReminderTime) {
        showError('驗證失敗', '啟用顧客前一天提醒時，請設定發送時間。');
        return false;
      }
      if (this.form.notifyCustomerDayBefore
        && !this.customerNotificationChannels.some(channel => channel.enabled)) {
        showError('驗證失敗', '啟用顧客前一天提醒時，請至少勾選一個通知管道。');
        return false;
      }
      return true;
    },
    validateConcurrencyLimit() {
      const value = Number(this.form.defaultConcurrencyLimit);
      if (Number.isInteger(value) && value >= 1 && value <= 20) return true;
      showError('驗證失敗', '設計師同時服務預設上限必須是 1 到 20 的整數。');
      return false;
    },
    shortTime(value) { return value ? String(value).slice(0, 5) : ''; },
    dateOnly(value) { return salonDateOnly(value); },
    wholeHour(value) {
      const match = this.shortTime(value).match(/^([01]\d|2[0-3])/);
      return `${match ? match[1] : '18'}:00`;
    },
    setPendingModeSwitch(settings) {
      this.pendingModeSwitch = settings?.pendingScheduleMode ? {
        mode: settings.pendingScheduleMode,
        effectiveFrom: salonDateOnly(settings.scheduleModeEffectiveFrom),
        confirmed: Boolean(settings.scheduleModeSwitchConfirmed)
      } : null;
      this.modeSwitchReadiness = (
        settings?.pendingScheduleMode && !settings.scheduleModeSwitchConfirmed
      ) ? (settings.modeSwitchReadiness || null) : null;
    },
    async showModeSwitchBlockers(readiness = {}) {
      const missing = readiness.missingSchedules || [];
      const conflicts = readiness.reservationConflicts || [];
      const missingNames = missing.map(item => item.stylistName).filter(Boolean);
      const missingHtml = missing.length
        ? `<div style="margin-top:14px;text-align:left">
             <h4 style="margin:0 0 6px">尚缺新模式班表（${missing.length} 位）</h4>
             <p style="margin:0">${escapeHtml(missingNames.join('、') || `${missing.length} 位設計師`)}</p>
           </div>`
        : '';
      const conflictRows = conflicts.map(item => `
        <tr>
          <td>${escapeHtml(item.reservationNumber || '-')}</td>
          <td>${escapeHtml(item.contactName || '未填姓名')}</td>
          <td>${escapeHtml(item.contactPhone || '-')}</td>
          <td>${escapeHtml(item.stylistName || '-')}</td>
          <td>${escapeHtml(this.dateOnly(item.appointmentDate))}</td>
          <td>${escapeHtml(this.shortTime(item.startTime))}－${escapeHtml(this.shortTime(item.endTime))}</td>
        </tr>
      `).join('');
      const conflictHtml = conflicts.length
        ? `<div style="margin-top:14px;text-align:left">
             <h4 style="margin:0 0 8px">衝突預約（${conflicts.length} 筆）</h4>
             <div style="overflow-x:auto;max-height:360px">
               <table style="width:100%;border-collapse:collapse;text-align:left;font-size:13px">
                 <thead>
                   <tr>
                     <th style="padding:6px 8px;border-bottom:1px solid #e5e7eb">預約編號</th>
                     <th style="padding:6px 8px;border-bottom:1px solid #e5e7eb">客人</th>
                     <th style="padding:6px 8px;border-bottom:1px solid #e5e7eb">電話</th>
                     <th style="padding:6px 8px;border-bottom:1px solid #e5e7eb">設計師</th>
                     <th style="padding:6px 8px;border-bottom:1px solid #e5e7eb">日期</th>
                     <th style="padding:6px 8px;border-bottom:1px solid #e5e7eb">時間</th>
                   </tr>
                 </thead>
                 <tbody>${conflictRows}</tbody>
               </table>
             </div>
           </div>`
        : '';
      await fireSwalDeferred({
        icon: 'warning',
        title: '切換計畫已儲存，但尚未確認',
        html: `<p style="margin:0;text-align:left">請先補齊新模式班表並處理下列衝突預約；完成後系統會自動確認切換。</p>
          ${missingHtml}${conflictHtml}`,
        width: conflicts.length ? 920 : 560,
        confirmButtonText: '我知道了',
        confirmButtonColor: '#d97706'
      });
    },
    async copyBookingUrl(link) {
      if (!link?.url) return;
      try {
        await navigator.clipboard.writeText(link.url);
      } catch {
        const input = document.createElement('textarea');
        input.value = link.url;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(input);
        if (!copied) {
          await showError('複製失敗', '無法複製到剪貼簿，請手動複製');
          return;
        }
      }
      this.copiedLinkKey = link.key;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        this.copiedLinkKey = '';
      }, 2000);
    },
    goBack() { this.$router.push({ name: 'SalonSettings' }); },
    async handleError(error, fallback) {
      await showError(fallback, error.response?.data?.message || error.message || fallback);
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_form-common.scss';
.salon-form-page { max-width: 1100px; margin: 0 auto; }
.store-summary {
  align-items: flex-start;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
  padding: 14px 16px;
}
.store-summary > i { color: #4f46e5; font-size: 28px; }
.store-summary > div { display: grid; flex: 1; gap: 3px; min-width: 0; }
.store-summary > div > span { color: #64748b; font-size: 13px; }
.booking-link { display: grid; gap: 6px; margin-top: 8px; min-width: 0; }
.booking-link-label { color: #64748b; font-size: 12px; }
.booking-link-hint { color: #b45309; font-size: 12px; }
.booking-link-field {
  align-items: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  gap: 8px;
  min-width: 0;
  padding: 4px 4px 4px 12px;
}
.booking-link-text {
  color: #334155;
  flex: 1;
  font-size: 13px;
  line-height: 1.4;
  min-width: 0;
  overflow-wrap: anywhere;
}
.booking-copy {
  align-items: center;
  background: #eef2ff;
  border: 0;
  border-radius: 6px;
  color: #4338ca;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  gap: 4px;
  height: 32px;
  padding: 0 10px;
}
.booking-copy:hover { background: #e0e7ff; }
.booking-copy.copied { background: #ecfdf5; color: #047857; }
.mode-switch-alert {
  align-items: center;
  border: 1px solid;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  margin-top: 16px;
  padding: 12px 14px;
}
.mode-switch-alert-block {
  align-items: stretch;
  display: grid;
  gap: 14px;
}
.mode-switch-alert-head {
  align-items: center;
  display: flex;
  gap: 12px;
}
.mode-switch-alert.warning { background: #fffbeb; border-color: #fcd34d; color: #92400e; }
.mode-switch-alert.confirmed { background: #f0fdf4; border-color: #86efac; color: #166534; }
.mode-switch-alert > i,
.mode-switch-alert-head > i { flex-shrink: 0; font-size: 22px; }
.mode-switch-alert > div,
.mode-switch-alert-head > div { display: grid; flex: 1; gap: 3px; }
.mode-switch-alert span { font-size: 13px; }
.mode-switch-detail {
  display: grid;
  gap: 8px;
}
.mode-switch-detail h4 {
  font-size: 14px;
  margin: 0;
}
.mode-switch-detail p {
  font-size: 13px;
  margin: 0;
}
.conflict-table-wrap {
  background: #fff;
  border: 1px solid #fde68a;
  border-radius: 8px;
  max-height: 320px;
  overflow: auto;
}
.conflict-table-wrap table {
  min-width: 760px;
  width: 100%;
}
.conflict-table-wrap th,
.conflict-table-wrap td {
  border-bottom: 1px solid #fef3c7;
  font-size: 13px;
  padding: 8px 10px;
  text-align: left;
  white-space: nowrap;
}
.conflict-table-wrap th {
  background: #fffbeb;
  color: #92400e;
  font-weight: 600;
  position: sticky;
  top: 0;
}
.tabs-nav {
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  gap: 20px;
  margin: 8px 0 20px;
  padding-left: 8px;
}
.tabs-nav-item {
  background: transparent;
  border: 0;
  border-bottom: 2px solid transparent;
  color: #64748b;
  cursor: pointer;
  padding: 10px 2px;
}
.tabs-nav-item.active { border-bottom-color: #4f46e5; color: #4f46e5; font-weight: 600; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.booking-switch-panel {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 22px;
}
.booking-switch {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  font-size: 15px;
  font-weight: 600;
  gap: 16px;
  justify-content: space-between;
  margin: 0;
  min-height: 64px;
  padding: 14px 16px;
}
.booking-switch.on {
  background: #eef2ff;
  border-color: #c7d2fe;
}
.toggle-switch {
  display: inline-block;
  flex-shrink: 0;
  height: 30px;
  position: relative;
  width: 52px;
}
.toggle-switch input {
  height: 0;
  opacity: 0;
  width: 0;
}
.toggle-slider {
  background: #cbd5e1;
  border-radius: 30px;
  cursor: pointer;
  inset: 0;
  position: absolute;
  transition: background 0.2s;
}
.toggle-slider::before {
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.25);
  content: '';
  height: 24px;
  left: 3px;
  position: absolute;
  top: 3px;
  transition: transform 0.2s;
  width: 24px;
}
.toggle-switch input:checked + .toggle-slider { background: #4f46e5; }
.toggle-switch input:checked + .toggle-slider::before { transform: translateX(22px); }
.toggle-switch input:focus-visible + .toggle-slider { outline: 2px solid #4f46e5; outline-offset: 2px; }
.check-label { align-items: center; display: inline-flex; gap: 6px; margin: 0; }
.notification-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-top: 20px;
  padding: 16px;
}
.notification-master { font-weight: 600; }
.reminder-time-field { margin: 14px 0 0; max-width: 220px; }
.notification-channel-list { display: grid; gap: 8px; margin-top: 14px; max-width: 520px; }
.notification-channel-list.disabled { opacity: 0.65; }
.notification-channel-row {
  align-items: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  display: grid;
  gap: 12px;
  grid-template-columns: 28px 1fr auto;
  padding: 9px 10px;
}
.channel-order {
  align-items: center;
  background: #eef2ff;
  border-radius: 50%;
  color: #4338ca;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  height: 24px;
  justify-content: center;
  width: 24px;
}
.channel-order-actions { display: flex; gap: 5px; }
.hours-table { margin-top: 16px; overflow-x: auto; }
.hours-table table { border-collapse: collapse; min-width: 820px; width: 100%; }
.hours-table th, .hours-table td { border-bottom: 1px solid #e5e7eb; padding: 12px; text-align: left; vertical-align: top; }
.hours-table th { background: #f8fafc; }
.day-name { font-weight: 600; min-width: 64px; }
.slots-container { display: grid; gap: 9px; }
.slot-row { align-items: center; display: flex; gap: 8px; }
.time-input { max-width: 150px; min-width: 120px; }
.slot-actions { display: flex; flex-wrap: wrap; gap: 7px; }
.closed-text { color: #94a3b8; }
.invalid-feedback { color: #dc2626; display: block; font-size: 12px; margin-top: 5px; }
.copy-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-top: 20px;
  padding: 16px;
}
.copy-panel-header { align-items: center; display: flex; justify-content: space-between; gap: 16px; }
.copy-panel-header h3 { font-size: 16px; margin: 0; }
.copy-targets { display: flex; flex-wrap: wrap; gap: 10px 18px; margin-top: 16px; }
.copy-target-tools { display: flex; gap: 10px; width: 100%; }
.btn-link { background: none; border: 0; color: #2563eb; cursor: pointer; padding: 0; }
.btn-link:disabled { color: #94a3b8; cursor: not-allowed; }
.copy-target { align-items: center; display: inline-flex; gap: 6px; }
.copy-selected-action { margin-top: 16px; }
@media (max-width: 768px) {
  .form-grid { grid-template-columns: 1fr; }
  .booking-switch-panel { grid-template-columns: 1fr; }
  .slot-row { align-items: stretch; flex-direction: column; }
  .time-input { max-width: none; width: 100%; }
  .copy-panel-header { align-items: stretch; flex-direction: column; }
}
</style>
